import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCity } from '../contexts/CityContext';
import api from '../services/api';
import { Loading } from '../components/Loading';
import { EmptyState } from '../components/EmptyState';

const VEHICLES = [
  { id: 'moto', label: 'Moto', emoji: '🛵' },
  { id: 'bike', label: 'Bicicleta', emoji: '🚲' },
  { id: 'car', label: 'Carro', emoji: '🚗' },
  { id: 'van', label: 'Van', emoji: '🚐' },
];

export function DeliveryProfilePage() {
  const { selectedCity, cities } = useCity();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [message, setMessage] = useState(null);
  const [form, setForm] = useState({
    city_id: selectedCity?.id || '',
    vehicle_type: 'moto',
    base_price: '6.00',
    price_per_km: '1.50',
    service_area_radius: '5000',
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/deliveries/me');
        const p = data.profile;
        setProfile(p);
        setForm({
          city_id: p.city_id ?? selectedCity?.id ?? '',
          vehicle_type: p.vehicle_type || 'moto',
          base_price: String(p.base_price ?? '6.00'),
          price_per_km: String(p.price_per_km ?? '1.50'),
          service_area_radius: String(p.service_area_radius ?? '5000'),
        });
      } catch {
        setProfile(null);
        setForm(prev => ({ ...prev, city_id: selectedCity?.id || prev.city_id }));
      } finally {
        setLoading(false);
      }
    })();
  }, []); // eslint-disable-line

  const set = (key) => (e) => setForm(prev => ({ ...prev, [key]: e.target.value }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const { data } = await api.post('/deliveries/profile', {
        city_id: Number(form.city_id),
        vehicle_type: form.vehicle_type,
        base_price: Number(form.base_price),
        price_per_km: Number(form.price_per_km),
        service_area_radius: Number(form.service_area_radius),
      });
      setProfile(data.profile);
      setMessage({ type: 'ok', text: 'Perfil de entregador salvo!' });
    } catch (err) {
      setMessage({ type: 'err', text: err.response?.data?.error?.message || 'Erro ao salvar perfil.' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async () => {
    setToggling(true);
    setMessage(null);
    try {
      const { data } = await api.post('/deliveries/toggle', {});
      setProfile(data);
    } catch (err) {
      setMessage({ type: 'err', text: err.response?.data?.error?.message || 'Erro ao alternar disponibilidade.' });
    } finally {
      setToggling(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid var(--nc-border)',
    fontSize: 14, marginBottom: 12, outline: 'none', background: 'var(--nc-surface)', color: 'var(--nc-text)', boxSizing: 'border-box'
  };
  const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--nc-text-secondary)', marginBottom: 6 };

  if (loading) return <Loading message="Carregando perfil..." />;

  return (
    <div className="nc-page">
      <div className="nc-pagehead">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <button onClick={() => navigate('/perfil')} aria-label="Voltar" className="nc-iconbtn">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="nc-page-title">Perfil de entregador</h1>
        </div>

        {/* Status de disponibilidade */}
        {profile ? (
          <button
            onClick={handleToggle}
            disabled={toggling}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, width: '100%',
              padding: '14px 16px', borderRadius: 12, border: 'none', cursor: toggling ? 'wait' : 'pointer',
              background: profile.is_available ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.12)',
              color: 'var(--nc-on-primary)', fontSize: 14, fontWeight: 600
            }}
          >
            <span className={profile.is_available ? 'nc-pulse' : ''} style={{
              width: 12, height: 12, borderRadius: '50%', flexShrink: 0,
              background: profile.is_available ? '#4ade80' : '#9ca3af'
            }} />
            {toggling ? 'Atualizando...' : profile.is_available ? 'Disponível para entregas — toque para pausar' : 'Indisponível — toque para ficar disponível'}
          </button>
        ) : (
          <p style={{ fontSize: 13, opacity: 0.85, margin: 0 }}>
            Crie seu perfil abaixo para começar a receber entregas na sua cidade.
          </p>
        )}
      </div>

      <div style={{ padding: 16 }}>
        {message && (
          <div role="status" style={{
            padding: '10px 12px', borderRadius: 8, marginBottom: 12, fontSize: 13,
            background: message.type === 'ok' ? '#f0fdf4' : '#fef2f2',
            border: `1px solid ${message.type === 'ok' ? '#bbf7d0' : '#fecaca'}`,
            color: message.type === 'ok' ? '#15803d' : '#b91c1c'
          }}>
            {message.text}
          </div>
        )}

        {profile && (
          <div style={{ background: 'var(--nc-surface)', borderRadius: 12, border: '1px solid var(--nc-border)', padding: 14, marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 48, height: 48, borderRadius: '50%', background: 'var(--nc-success-soft)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24
              }}>🛵</div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--nc-text)' }}>
                  {VEHICLES.find(v => v.id === profile.vehicle_type)?.label || profile.vehicle_type}
                </div>
                <div className="nc-hint">
                  {profile.city_name || profile.city_id ? `Cidade ${profile.city_name || ''}` : ''} • Raio de {profile.service_area_radius}m
                </div>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSave} style={{ background: 'var(--nc-surface)', borderRadius: 14, border: '1px solid var(--nc-border)', padding: 18 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--nc-text)', marginBottom: 12 }}>
            {profile ? 'Atualizar dados' : 'Criar perfil'}
          </div>

          <label style={labelStyle}>Cidade de atuação</label>
          <select value={form.city_id} onChange={set('city_id')} required style={inputStyle} aria-label="Cidade">
            <option value="" disabled>Selecione a cidade</option>
            {cities.map(c => <option key={c.id} value={c.id}>{c.name}/{c.state}</option>)}
          </select>

          <label style={labelStyle}>Veículo</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 12 }}>
            {VEHICLES.map(v => (
              <button
                key={v.id}
                type="button"
                onClick={() => setForm(prev => ({ ...prev, vehicle_type: v.id }))}
                aria-pressed={form.vehicle_type === v.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 10,
                  border: form.vehicle_type === v.id ? '1.5px solid #0d5c3b' : '1px solid var(--nc-border)',
                  background: form.vehicle_type === v.id ? '#f0fdf4' : '#fff',
                  color: form.vehicle_type === v.id ? '#0d5c3b' : '#374151',
                  fontSize: 13, cursor: 'pointer'
                }}
              >
                <span style={{ fontSize: 16 }}>{v.emoji}</span> {v.label}
              </button>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label style={labelStyle}>Taxa base (R$)</label>
              <input type="number" step="0.01" min="0" value={form.base_price} onChange={set('base_price')} style={inputStyle} aria-label="Taxa base" />
            </div>
            <div>
              <label style={labelStyle}>R$ por km</label>
              <input type="number" step="0.01" min="0" value={form.price_per_km} onChange={set('price_per_km')} style={inputStyle} aria-label="Preço por km" />
            </div>
          </div>

          <label style={labelStyle}>Raio de atendimento (metros)</label>
          <input type="number" step="100" min="100" value={form.service_area_radius} onChange={set('service_area_radius')} style={inputStyle} aria-label="Raio de atendimento" />

          <button type="submit" disabled={saving} className="nc-btn-primary" style={{ width: '100%' }}>
            {saving ? 'Salvando...' : profile ? 'Salvar alterações' : 'Criar perfil'}
          </button>
        </form>
      </div>
    </div>
  );
}
